import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../../shared/hooks/useApi";
import { VisitInstanceId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";

/**
 * Response of
 * `GET .../sandata_visits/:visitInstanceId/submission_attempts`.
 *
 * Declared here rather than imported from the generated schema: the checked-in
 * codegen predates this endpoint, so `api.get` cannot type it yet and
 * `validate-message` finds no validator for the path. Two consequences worth
 * knowing while that is true:
 *
 * - The response is not validated, so it is also not *revived* — every
 *   `Instant` arrives as an ISO string rather than a js-joda object. Both are
 *   accepted below, so this keeps working after `pnpm openapi:local` swaps one
 *   for the other.
 * - Once regenerated, these types and the untyped call can both go.
 */
type ApiInstant = string | { toString: () => string };

export interface SandataVisitSubmissionAttempt {
  batchId: number;
  batchUuid: string | null;
  status: "PENDING" | "COMPLETED" | "ERROR";
  source: string;
  sandataState: string;
  submittedAt: ApiInstant;
  resultCheckedAt: ApiInstant | null;
  visitCount: number;
  successCount: number | null;
  errorCount: number | null;
  submittedByName: string | null;
  sentPayload: unknown | null;
}

export interface SandataVisitSubmissionAttemptsResponse {
  attempts: SandataVisitSubmissionAttempt[];
  currentError: { attemptedAt: ApiInstant; errors: string[] } | null;
}

const statusColorScheme: Record<SandataVisitSubmissionAttempt["status"], string> = {
  PENDING: "orange",
  COMPLETED: "green",
  ERROR: "red",
};

function formatInstant(value: ApiInstant): string {
  try {
    return dateFormatter.toDateTime(String(value));
  } catch {
    return String(value);
  }
}

function formatOutcome(attempt: SandataVisitSubmissionAttempt): string | null {
  if (attempt.status === "PENDING") {
    return "Awaiting result from Sandata";
  }

  if (attempt.successCount === null && attempt.errorCount === null) {
    return null;
  }

  // Batch-level, not visit-level: Sandata's verdict is not recorded per visit,
  // so these counts describe every visit in the batch, including this one.
  return `${attempt.successCount ?? 0} accepted / ${
    attempt.errorCount ?? 0
  } rejected in this batch`;
}

interface SandataPayloadModalContentProps {
  visitInstanceId: VisitInstanceId;
}

export function SandataPayloadModalContent({ visitInstanceId }: SandataPayloadModalContentProps) {
  const { api } = useApi();
  const toast = useToast();

  const query = useQuery({
    queryKey: ["sandata_submission_attempts", visitInstanceId],
    queryFn: async (): Promise<SandataVisitSubmissionAttemptsResponse> => {
      // Untyped path until the endpoint reaches the generated schema — the same
      // escape hatch the HHAX re-submit call on this page uses.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return await (api as any).get("./sandata_visits/:visitInstanceId/submission_attempts", {
        path: { visitInstanceId },
      });
    },
  });

  const handleCopy = (payload: unknown) => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(
      () => toast({ title: "Payload copied", status: "success", duration: 2000 }),
      () => toast({ title: "Could not copy payload", status: "error", duration: 4000 })
    );
  };

  if (query.isLoading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  if (query.isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        Could not load submission attempts.
      </Alert>
    );
  }

  const { attempts, currentError } = query.data ?? { attempts: [], currentError: null };

  if (attempts.length === 0) {
    return (
      <Alert status="info">
        <AlertIcon />
        This visit has not been submitted to Sandata yet.
      </Alert>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      {currentError !== null && (
        <Alert alignItems="flex-start" status="error">
          <AlertIcon />
          <Box>
            {/* Deliberately not attached to one attempt: the error table keeps a
                single row per visit, overwritten by each result, so it belongs
                to whichever attempt was answered last. */}
            <Text fontWeight="bold">
              Latest result from Sandata ({formatInstant(currentError.attemptedAt)})
            </Text>
            {currentError.errors.map((error, index) => (
              <Text key={index} fontSize="sm">
                • {error}
              </Text>
            ))}
          </Box>
        </Alert>
      )}

      <Accordion allowToggle defaultIndex={[0]}>
        {attempts.map((attempt, index) => {
          const outcome = formatOutcome(attempt);

          return (
            <AccordionItem key={attempt.batchId}>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <HStack flexWrap="wrap" spacing={2}>
                    <Text fontWeight="bold">
                      Attempt {attempts.length - index} of {attempts.length}
                    </Text>
                    <Badge colorScheme={statusColorScheme[attempt.status]}>{attempt.status}</Badge>
                    <Badge colorScheme="gray">{attempt.source}</Badge>
                    <Text color="gray.600" fontSize="sm">
                      {formatInstant(attempt.submittedAt)}
                    </Text>
                    {attempt.submittedByName !== null && (
                      <Text color="gray.600" fontSize="sm">
                        by {attempt.submittedByName}
                      </Text>
                    )}
                  </HStack>
                  <HStack color="gray.500" flexWrap="wrap" fontSize="xs" mt={1} spacing={2}>
                    <Text>
                      {attempt.batchUuid === null
                        ? "Rejected on submission — no batch created"
                        : `Batch ${attempt.batchUuid}`}
                    </Text>
                    <Text>State {attempt.sandataState}</Text>
                    {outcome !== null && <Text>{outcome}</Text>}
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {attempt.sentPayload === null ? (
                  <Alert status="info">
                    <AlertIcon />
                    No payload was recorded for this attempt — it pre-dates payload logging.
                  </Alert>
                ) : (
                  <>
                    <Flex justify="flex-end" mb={2}>
                      <Button size="xs" onClick={() => handleCopy(attempt.sentPayload)}>
                        Copy JSON
                      </Button>
                    </Flex>
                    <Code
                      display="block"
                      fontSize="xs"
                      maxH="400px"
                      overflow="auto"
                      p={3}
                      whiteSpace="pre"
                      width="100%"
                    >
                      {JSON.stringify(attempt.sentPayload, null, 2)}
                    </Code>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    </VStack>
  );
}
